import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/apiService';
import { Contract, ethers } from 'ethers';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';
import { gameContractABI, gameContractAddress, tokenContractABI, tokenContractAddress } from '../contract';
import { useEthersSigner } from './ethers';
import { queryKeys } from './queries';
import socketService from '../services/socketService';
import useAppStore from '../stores/appStore';

// Wallet Mutations - Now handled by Wagmi/AppKit
export const useConnectWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // This is now handled by AppKit modal
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate and refetch wallet connection query
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet.connection() });
      console.log('Wallet connection initiated');
    },
    onError: (error) => {
      console.error('Failed to connect wallet:', error);
    },
  });
};

export const useDisconnectWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // This is now handled by AppKit modal
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate all wallet-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet.all });
      console.log('Wallet disconnection initiated');
    },
    onError: (error) => {
      console.error('Failed to disconnect wallet:', error);
    },
  });
};

export const useMutateClaimReward = () => {
  const queryClient = useQueryClient();
  const { address } = useAccount();

  return useMutation({
    mutationFn: async ({ gameId }) => {
      if (!address) {
        throw new Error('Wallet not connected');
      }

      try {
        toast.info('Claiming rewards...');

        const response = await api.post('/api/rewards/claim', {
          gameId,
          walletAddress: address,
        });

        const data = response.data;

        if (data.success) {
          toast.success('Rewards claimed successfully!');
          return data;
        } else {
          throw new Error(data.error || 'Failed to claim rewards');
        }
      } catch (error) {
        console.error('❌ Error claiming rewards:', error);
        toast.error('Failed to claim rewards');
        throw error;
      }
    },
    onSuccess: (data) => {
      // Invalidate related queries to refresh the data
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet.rewards(address) });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.stats(address) });

      console.log('Reward claimed successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to claim reward:', error);
    },
  });
};

// Payment Mutations
export const useMutatePayToStart = () => {
  const { currentRoom } = useAppStore();
  const queryClient = useQueryClient();
  const { address } = useAccount();
  const signer = useEthersSigner(97);

  const getReadablePayErrorMessage = (error) => {
    const code = error?.code ?? error?.error?.code;
    const rawMsg =
      error?.response?.data?.error ??
      error?.shortMessage ??
      error?.reason ??
      error?.data?.message ??
      error?.message ??
      '';

    const msg = typeof rawMsg === 'string' ? rawMsg : String(rawMsg || '');
    const msgLower = msg.toLowerCase();

    // Ethers v5 often uses code: "ACTION_REJECTED" for wallet rejects
    if (code === 4001 || code === 'ACTION_REJECTED' || msgLower.includes('user rejected')) {
      return 'Transaction rejected in your wallet. Please approve to continue.';
    }

    if (code === 'TRANSACTION_REPLACED' && error?.cancelled) {
      return 'Transaction was cancelled in your wallet.';
    }

    if (msgLower.includes('insufficient funds')) {
      return 'Insufficient funds to pay for gas/fees.';
    }

    if (msgLower.includes('nonce') && (msgLower.includes('too low') || msgLower.includes('already been used'))) {
      return 'Network nonce issue. Please wait a few seconds and try again.';
    }

    if (msgLower.includes('metamask') && msgLower.includes('not found')) {
      return 'Wallet not found. Please install or enable MetaMask.';
    }

    // Remove the long ethers details blob:
    // "user rejected transaction (action="sendTransaction", transaction=..., code=..., version=...)"
    if (msg.includes(' (action=')) {
      return msg.split(' (action=')[0].trim() || 'Payment failed. Please try again.';
    }

    return msg.trim() || 'Payment failed. Please try again.';
  };

  const mutateFn = async ({ gameId, username, depositFee, platformFee, onProgress }) => {
    if (!address) {
      throw new Error('Wallet not connected');
    }

    if (!window.ethereum) {
      throw new Error('MetaMask not found');
    }

    try {
      // Initialize progress callback
      const updateProgress = (step, status) => {
        if (onProgress) {
          onProgress({ step, status });
        }
      };

      const tokenContract = new Contract(tokenContractAddress, tokenContractABI, signer);
      const gameContract = new Contract(gameContractAddress, gameContractABI, signer);

      updateProgress('approve', 'Approving tokens for game contract...');

      const totalPrice = ethers.BigNumber.from(depositFee);
      const approveTx = await tokenContract.approve(gameContractAddress, totalPrice);
      console.log("🚀 ~ mutateFn ~ approveTx:", approveTx)
      updateProgress('approve', 'Waiting for approval transaction to be mined...');
      const approveReceipt = await approveTx.wait();

      updateProgress('approve', 'Token approval completed!');
      updateProgress('payment', 'Processing room payment...');

      // Step 2: Join game
      // const joinTx = await writeContract({
      //   abi: gameContractABI,
      //   address: gameContractAddress,
      //   functionName: 'joinGame',
      //   args: [gameId],
      // });
      const joinTx = await gameContract.joinGame(currentRoom);
      updateProgress('payment', 'Waiting for join game transaction to be mined...');
      const joinReceipt = await joinTx.wait();

      updateProgress('payment', 'Room payment completed!');
      updateProgress('saving', 'Saving game data...');

      
      const gameData = {
        gameId: gameId.toString(),
        platformFee,
        depositFee,
        totalPrice: totalPrice.toString(),
        approveTxHash: approveTx.hash,
        joinTxHash: joinTx.hash,
        timestamp: new Date().toISOString(),
        status: 'joined',
        walletAddress: address,
        username,
        // Add socketId - this is required by the backend to find the player record
        roomId: currentRoom,
        socketId: socketService.socket?.id || null,
      };
     
      
      // Save to backend
      console.log('🔄 Sending game transaction data to backend:', gameData);
      
      const response = await api.post('/api/game-transactions', gameData);
      console.log('✅ Backend response:', response.data);
      
      if (!response.data) {
        throw new Error('Failed to save game data');
      }
      updateProgress('saving', 'game data saved successfully...');
      
      if (!response.data.message || response.data.message !== 'Game transaction stored successfully') {
        console.warn('⚠️ Unexpected backend response format:', response.data);
      }

      // Save to localStorage for session recovery
      localStorage.setItem('user_transaction_Data', JSON.stringify(gameData));

      updateProgress('completed', 'Payment successful! You can now start the game.');

      toast.success('Payment successful! Game joined successfully.');

      return {
        success: true,
        gameData,
        transactions: {
          approve: {
            hash: approveTx.hash,
            receipt: approveReceipt,
          },
          join: {
            hash: joinTx.hash,
            receipt: joinReceipt,
          },
        },
      };
    } catch (error) {
      console.error('❌ Error in payToStart:', error);
      const errorMessage = getReadablePayErrorMessage(error);

      // toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  return useMutation({
    mutationFn: mutateFn,
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.user.stats(address) });
      queryClient.invalidateQueries({ queryKey: queryKeys.wallet.rewards(address) });
      queryClient.invalidateQueries({ queryKey: queryKeys.game.rooms() });

      console.log('Payment completed successfully:', data);
    },
    onError: (error) => {
      console.error('Payment failed:', error);
      toast.error(error?.message || 'Payment failed. Please try again.');
    },
  });
};

// Game Mutations
export const useCreateRoom = () => {
  const queryClient = useQueryClient();
  const { address } = useAccount();

  return useMutation({
    mutationFn: async ({ roomSettings, isPrivate = false }) => {
      // This would call your backend API to create a room
      const response = await api.post('/api/rooms', {
        settings: roomSettings,
        isPrivate,
        creator: address,
      });

      if (!response.data) {
        throw new Error('Failed to create room');
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate rooms list
      queryClient.invalidateQueries({ queryKey: queryKeys.game.rooms() });

      console.log('Room created successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to create room:', error);
    },
  });
};

export const useJoinRoom = () => {
  const queryClient = useQueryClient();
  const { address } = useAccount();

  return useMutation({
    mutationFn: async ({ roomId, username }) => {
      // This would call your backend API to join a room
      const response = await api.post(`/api/rooms/${roomId}/join`, {
        username,
        walletAddress: address,
      });

      if (!response.data) {
        throw new Error('Failed to join room');
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate room and players queries
      queryClient.invalidateQueries({ queryKey: queryKeys.game.room(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.game.players(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.game.rooms() });

      console.log('Joined room successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to join room:', error);
    },
  });
};

export const useLeaveRoom = () => {
  const queryClient = useQueryClient();
  const { address } = useAccount();

  return useMutation({
    mutationFn: async ({ roomId }) => {
      // This would call your backend API to leave a room
      const response = await api.post(`/api/rooms/${roomId}/leave`, {
        walletAddress: address,
      });

      if (!response.data) {
        throw new Error('Failed to leave room');
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate room and players queries
      queryClient.invalidateQueries({ queryKey: queryKeys.game.room(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.game.players(variables.roomId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.game.rooms() });

      console.log('Left room successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to leave room:', error);
    },
  });
};

// User Mutations
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const { address } = useAccount();

  return useMutation({
    mutationFn: async ({ profileData }) => {
      // This would call your backend API to update user profile
        const response = await api.put('/api/user/profile', {
          ...profileData,
          walletAddress: address,
        });

      if (!response.data) {
        throw new Error('Failed to update profile');
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate user profile query
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(address) });

      console.log('Profile updated successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to update profile:', error);
    },
  });
};

// Utility function to create optimistic updates
export const createOptimisticUpdate = (queryClient, queryKey, updater) => {
  // Cancel any outgoing refetches
  queryClient.cancelQueries({ queryKey });

  // Snapshot the previous value
  const previousData = queryClient.getQueryData(queryKey);

  // Optimistically update to the new value
  queryClient.setQueryData(queryKey, updater);

  // Return a context object with the snapshotted value
  return { previousData };
};
